import { Component, Input, OnDestroy } from '@angular/core';
import { FilesService } from '@shared/components/files/shared/files.service';
import { Subject } from 'rxjs';
import { FilesActions } from '@shared/components/files/shared/models/action.model';
import { ActionType } from '@shared/components/files/shared/action-type.enum';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { FilesActionRuleService } from '../../shared/files-action-rule.service';

@Component({
  selector: 'oes-files-action',
  templateUrl: './action.component.html',
  styleUrls: ['./action.component.scss'],
})
export class FilesActionComponent implements OnDestroy {
  @Input() accessControl: FilesActions;
  @Input() readOnly: boolean = false;

  actionType = ActionType;

  private highlightedNodes: any;
  private ngUnsubscribe: Subject<any> = new Subject();
  private selectedNodes: any;

  constructor(
    private _filesService: FilesService,
    private _translateService: TranslateService,
    private _filesActionRuleService: FilesActionRuleService
  ) {
    this._filesService.selectedNodes$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((selectedNodes: any) => {
        this.selectedNodes = selectedNodes;
      });

    this._filesService.highlightedNodes$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((highlightedNodes: any) => {
        this.highlightedNodes = highlightedNodes;
      });
  }

  ngOnDestroy() {
    this._filesService.clear();
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }

  emitAction(type: ActionType) {
    this._filesService.clickActionButton(type);
  }

  // We don't create a new folder under multiple places.
  disableCreateFolder(): boolean {
    return !this.accessControl.create;
  }

  disableUpload(): boolean {
    return !this.accessControl.upload;
  }

  disableMove(): boolean {
    return !this._filesActionRuleService.canMove(this.accessControl, this.selectedNodes);
  }

  disableCopy(): boolean {
    return !this._filesActionRuleService.canCopy(this.accessControl, this.selectedNodes);
  }

  get selectedFileCount(): number {
    return this._filesActionRuleService.getSelectedFileCount(this.selectedNodes);
  }

  get selectedTotalSize(): number {
    return this._filesActionRuleService.getSelectedTotalSize(this.selectedNodes);
  }

  get exceedsSizeLimit(): boolean {
    return this.selectedTotalSize > this._filesActionRuleService.maxTotalBytes;
  }

  get exceedsAnyLimit(): boolean {
    return this.exceedsSizeLimit;
  }

  get downloadTooltip(): string {
    if (this.exceedsSizeLimit) {
      return this._translateService.instant('data-room.download-limit.total-size', {
        max: '50 GB',
      });
    }
    return '';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(1)) + ' ' + units[i];
  }

  disableDownload(): boolean {
    return !this._filesActionRuleService.canDownload(this.accessControl, this.selectedNodes);
  }

  disableDelete(): boolean {
    return !this._filesActionRuleService.canDelete(this.accessControl, this.selectedNodes);
  }

  disableTag(): boolean {
    return !(this.accessControl.tag && this.selectedNodes?.length > 0);
  }

  // can add tags to multiple documents
  disableTagAdd(): boolean {
    return !this._filesActionRuleService.canTagAdd(this.accessControl, this.highlightedNodes);
  }

  // only can delete tags one document at a time
  disableTagDelete(): boolean {
    return !this._filesActionRuleService.canTagDelete(this.accessControl, this.highlightedNodes);
  }

  disableAssociation(): boolean {
    return !(this.accessControl.tag && this.selectedNodes?.length > 0);
  }

  // can add associations to multiple documents
  disableAssociationAdd(): boolean {
    return !this._filesActionRuleService.canAssociationAdd(this.accessControl, this.highlightedNodes);
  }

  // only can delete associations one document at a time
  disableAssociationDelete(): boolean {
    return !this._filesActionRuleService.canAssociationDelete(
      this.accessControl,
      this.highlightedNodes,
      this.selectedNodes
    );
  }

  disableEditName(): boolean {
    return !this._filesActionRuleService.canEditName(
      this.accessControl,
      this.highlightedNodes,
      this.selectedNodes
    );
  }

  disablePreview(): boolean {
    return !this._filesActionRuleService.canPreview(this.accessControl, this.highlightedNodes);
  }
}
